import { ApolloClient, ApolloLink, InMemoryCache, createHttpLink, split, Observable, type DocumentNode } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { authService } from '@features/auth/services/authService';
import { refreshToken } from './refreshToken';
import { navigateToLogin } from '@utils/navigateToLogin';
import { typePolicies } from './cacheConfig';
import siteConfig from '@config';
import { init, type GqlExecutor } from '@hopdrive/sdk-core';

const httpLink = createHttpLink({
  uri: siteConfig.graphqlHttpUrl,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: siteConfig.graphqlWsUrl,
    connectionParams: async () => {
      return {
        headers: {
          authorization: `Bearer ${await authService.getAccessTokenWithAutoRefresh()}`,
          'hasura-client-name': siteConfig.graphqlClientName,
        },
      };
    },
  })
);

const authLink = setContext(async (_, { headers }) => {
  return {
    headers: {
      ...headers,
      authorization: `Bearer ${await authService.getAccessTokenWithAutoRefresh()}`,
      'hasura-client-name': siteConfig.graphqlClientName,
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === 'UNAUTHENTICATED' || err.message.includes('JWTExpired')) {
        // Return an Observable that handles the async token refresh
        return new Observable(observer => {
          refreshToken()
            .then(newToken => {
              const oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  authorization: `Bearer ${newToken}`,
                },
              });
              // Subscribe to the retried operation and forward results to the observer
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              };
              forward(operation).subscribe(subscriber);
            })
            .catch(error => {
              console.error('Error refreshing token:', error);
              navigateToLogin();
              observer.error(error);
            });
        });
      }
    }
  }

  if (networkError) {
    console.error(`[Network Error]: ${networkError.message}`);
  }

  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    navigateToLogin();
  }
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
  },
  wsLink,
  httpLink
);

const link = ApolloLink.from([errorLink, authLink, splitLink]);

const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    typePolicies,
  }),
});

/**
 * App-owned Apollo executor: a thin adapter over this app's ApolloClient,
 * registered as sdk-core's default so every sdk-* package and the colocated
 * generated wrappers run through it. Reads use network-only (imperative callers
 * want fresh data); React hooks keep Apollo's cache-first default.
 */
export const apolloSdkExecutor: GqlExecutor = {
  query: async (document, variables) =>
    (
      await client.query({
        query: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
        fetchPolicy: 'network-only',
      })
    ).data,
  mutate: async (document, variables) =>
    (
      await client.mutate({
        mutation: document as unknown as DocumentNode,
        variables: variables as Record<string, unknown>,
      })
    ).data,
};

// One core init covers every package whose init just forwards the executor to
// core (configs, ar, events, dispatch, appointments). logger: console keeps the
// SDK's operational logging on (core's default logger is a silent no-op).
init({ executor: apolloSdkExecutor, logger: console });

export default client;
